import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  Pagination,
  CircularProgress,
  Alert
} from "@mui/material";
import { postData, getData, deleteData } from "../../Webservice/Webservice";
import { RiDeleteBin6Fill, RiEdit2Fill } from "react-icons/ri";
import { toast } from "react-toastify";

function FAQs() {
    const [openTopicModal, setOpenTopicModal] = useState(false);
  const [openFaqModal, setOpenFaqModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [name, setName] = useState("");
  const [topicPosition, setTopicPosition] = useState(0);
  const [faqData, setFaqData] = useState({
    topic: "",
    question: "",
    answer: "",
    position: 0
  });
  const [topics, setTopics] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  
  // Pagination state
  const PAGE_SIZE = 10;
  const [topicMeta, setTopicMeta] = useState({ 
    page: 1, 
    totalPages: 1, 
    total: 0 
  });
  const [faqMeta, setFaqMeta] = useState({ 
    page: 1, 
    totalPages: 1, 
    total: 0 
  });
  const [loading, setLoading] = useState({
    topics: false,
    faqs: false
  });
  const [isPaginating, setIsPaginating] = useState({
    topics: false,
    faqs: false
  });
  const [error, setError] = useState({
    topics: null,
    faqs: null
  });

  // Pagination handlers
  const handleTopicPageChange = (event, newPage) => {
    if (newPage === topicMeta.page) return;
    setIsPaginating(prev => ({ ...prev, topics: true }));
    setTopicMeta((prev) => ({ ...prev, page: newPage }));
    setIsPaginating(prev => ({ ...prev, topics: false }));
  };

  const handleFaqPageChange = (event, newPage) => {
    if (newPage === faqMeta.page) return;
    setIsPaginating(prev => ({ ...prev, faqs: true }));
    setFaqMeta((prev) => ({ ...prev, page: newPage }));
    setIsPaginating(prev => ({ ...prev, faqs: false }));
  };

  // Fetch topics from API with pagination
  const fetchTopics = async (page = 1) => {
    try {
      setLoading(prev => ({ ...prev, topics: page === 1 }));
      setIsPaginating(prev => ({ ...prev, topics: page !== 1 }));
      setError(prev => ({ ...prev, topics: null }));
      
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      
      if (!token) {
        setError(prev => ({ ...prev, topics: 'Authentication token not found. Please login again.' }));
        setLoading(prev => ({ ...prev, topics: false }));
        setIsPaginating(prev => ({ ...prev, topics: false }));
        return [];
      }

      const params = {
        page,
        limit: PAGE_SIZE
      };
      
      const response = await getData("faqtopic/list", params, token);
      
      if (response.topics && Array.isArray(response.topics)) {
        const fetchedTopics = response.topics.map(topic => ({
          id: topic._id,
          name: topic.name,
          isActive: topic.isActive,
          position: topic.position || 0
        }));
        setTopics(fetchedTopics);
        
        // Update pagination meta
        const metaData = response.meta || { 
          page: 1, 
          totalPages: 1, 
          total: fetchedTopics.length 
        };
        setTopicMeta({
          page: metaData.page || 1,
          totalPages: metaData.totalPages || 1,
          total: metaData.total || 0
        });
        
        if (fetchedTopics.length > 0 && !selectedTopic) {
          setSelectedTopic(fetchedTopics[0].id);
        }
        return fetchedTopics;
      } else {
        setTopics([]);
        setTopicMeta({ page: 1, totalPages: 1, total: 0 });
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch topics:", error);
      setError(prev => ({ ...prev, topics: `Failed to fetch topics: ${error.message}. Please try again.` }));
      setTopics([]);
      setTopicMeta({ page: 1, totalPages: 1, total: 0 });
      return [];
    } finally {
      setLoading(prev => ({ ...prev, topics: false }));
      setIsPaginating(prev => ({ ...prev, topics: false }));
    }
  };

  // Fetch FAQs from API with pagination
  const fetchFaqs = async (topicId = selectedTopic, page = 1) => {
    if (!topicId) return;
    
    try {
      setLoading(prev => ({ ...prev, faqs: page === 1 }));
      setIsPaginating(prev => ({ ...prev, faqs: page !== 1 }));
      setError(prev => ({ ...prev, faqs: null }));
      
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      
      if (!token) {
        setError(prev => ({ ...prev, faqs: 'Authentication token not found. Please login again.' }));
        setLoading(prev => ({ ...prev, faqs: false }));
        setIsPaginating(prev => ({ ...prev, faqs: false }));
        return;
      }

      const params = {
        page,
        limit: PAGE_SIZE
      };
      
      const response = await getData(`faq/list/${topicId}`, params, token);
      
      if (response.faqs && Array.isArray(response.faqs)) {
        const fetchedFaqs = response.faqs.map(faq => ({
          id: faq._id,
          topic: faq.topic,
          question: faq.question,
          answer: faq.answer,
          isActive: faq.isActive,
          position: faq.position || 0
        }));
        setFaqs(fetchedFaqs);
        
        // Update pagination meta
        const metaData = response.meta || { 
          page: 1, 
          totalPages: 1, 
          total: fetchedFaqs.length 
        };
        setFaqMeta({
          page: metaData.page || 1,
          totalPages: metaData.totalPages || 1,
          total: metaData.total || 0
        });
      } else {
        setFaqs([]);
        setFaqMeta({ page: 1, totalPages: 1, total: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch FAQs:", error);
      setError(prev => ({ ...prev, faqs: `Failed to fetch FAQs: ${error.message}. Please try again.` }));
      setFaqs([]);
      setFaqMeta({ page: 1, totalPages: 1, total: 0 });
    } finally {
      setLoading(prev => ({ ...prev, faqs: false }));
      setIsPaginating(prev => ({ ...prev, faqs: false }));
    }
  };

  // Fetch topics on component mount and page change
  useEffect(() => {
    fetchTopics(topicMeta.page);
  }, [topicMeta.page]);

  // Fetch FAQs when selected topic or page changes
  useEffect(() => {
    if (selectedTopic) {
      // Reset to page 1 when topic changes
      setFaqMeta(prev => ({ ...prev, page: 1 }));
    }
  }, [selectedTopic]);

  useEffect(() => {
    if (selectedTopic) {
      fetchFaqs(selectedTopic, faqMeta.page);
    }
  }, [selectedTopic, faqMeta.page]);

  const handleOpenTopicModal = () => {
    setIsEditMode(false);
    setEditingId("");
    setName("");
    setTopicPosition(0);
    setOpenTopicModal(true);
  };

  const handleCloseTopicModal = () => {
    setOpenTopicModal(false);
    setName("");
    setTopicPosition(0);
    setIsEditMode(false);
    setEditingId("");
  };

  const handleOpenFaqModal = () => {
    setIsEditMode(false);
    setEditingId("");
    setFaqData({ topic: selectedTopic, question: "", answer: "", position: 0 });
    setOpenFaqModal(true);
  };

  const handleCloseFaqModal = () => {
    setOpenFaqModal(false);
    setFaqData({ topic: selectedTopic, question: "", answer: "", position: 0 });
    setIsEditMode(false);
    setEditingId("");
  };

  // Handle edit topic - populate form with existing data
  const handleEditTopic = topic => {
    setIsEditMode(true);
    setEditingId(topic.id);
    setName(topic.name);
    setTopicPosition(topic.position || 0);
    setOpenTopicModal(true);
  };

  // Handle edit FAQ - populate form with existing data
  const handleEditFaq = faq => {
    setIsEditMode(true);
    setEditingId(faq.id);
    setFaqData({
      topic: faq.topic,
      question: faq.question,
      answer: faq.answer,
      position: faq.position || 0
    });
    setOpenFaqModal(true);
  };

  const handleTopicSubmit = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (isEditMode) {
        // Update existing topic
        const updateData = { name, position: Number(topicPosition) };
        await postData(`faqtopic/update/${editingId}`, updateData, token);
        // Refresh topics list (reset to page 1)
        setTopicMeta(prev => ({ ...prev, page: 1 }));
        await fetchTopics(1);
        // Show success message
        toast.success("Topic updated successfully.");
      } else {
        // Create new topic
        const res = await postData("faqtopic/add", { name, position: Number(topicPosition) }, token);
        
        // Refresh topics list (reset to page 1)
        setTopicMeta(prev => ({ ...prev, page: 1 }));
        const updatedTopics = await fetchTopics(1);
        
        // Try to get ID from response or fallback to finding it in the updated list
        let newTopicId = res._id || res.id || (res.data && res.data._id) || (res.topic && res.topic._id);
        
        if (!newTopicId && updatedTopics && updatedTopics.length > 0) {
            // Fallback: try to find by name
            const match = updatedTopics.find(t => t.name === name);
            if (match) newTopicId = match.id;
        }

        if (newTopicId) {
            setSelectedTopic(newTopicId);
        } else if (updatedTopics && updatedTopics.length > 0 && !selectedTopic) {
             // If still no ID found but we have topics, select the first one (fetchTopics might have done this already, but to be sure)
             setSelectedTopic(updatedTopics[0].id);
        }

        // Show success message
        toast.success("Topic added successfully.");
      }

      handleCloseTopicModal();
    } catch (error) {
      console.error("Failed to save topic:", error);
      isEditMode ? toast.error("Failed to update topic. Please try again.") : toast.error("Failed to add topic. Please try again.");
    }
  };

  const handleFaqSubmit = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (isEditMode) {
        // Update existing FAQ
        await postData(`faq/update/${editingId}`, faqData, token);
        // Refresh FAQs for current topic (reset to page 1)
        setFaqMeta(prev => ({ ...prev, page: 1 }));
        await fetchFaqs(selectedTopic, 1);
        // Show success message
        toast.success("FAQ updated successfully.");
      } else {
        // Create new FAQ
        const res = await postData("faq/add", faqData, token);
        // Refresh FAQs for current topic (reset to page 1)
        setFaqMeta(prev => ({ ...prev, page: 1 }));
        await fetchFaqs(selectedTopic, 1);
        // Show success message
        toast.success("FAQ added successfully.");
      }

      handleCloseFaqModal();
    } catch (error) {
      console.error("Failed to save FAQ:", error);
      isEditMode ? toast.error("Failed to update FAQ. Please try again.") : toast.error("Failed to add FAQ. Please try again.");
    }
  };

  // Delete FAQ handler
  const handleDeleteFaq = async faqId => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      await deleteData(`faq/delete/${faqId}`, token);
      // Refresh FAQs for current topic (keep current page)
      await fetchFaqs(selectedTopic, faqMeta.page);
      // Show success message
      toast.success("FAQ deleted successfully.");
    } catch (error) {
      console.error("Failed to delete FAQ:", error);
      toast.success("Failed to delete FAQ. Please try again.");
    }
  };

  // Delete Topic handler
  const handleDeleteTopic = async topicId => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      await deleteData(`faqtopic/delete/${topicId}`, token);
      // Refresh topics list (keep current page)
      await fetchTopics(topicMeta.page);
      
      // Handle selected topic
      if (selectedTopic === topicId) {
        const remainingTopics = topics.filter(topic => topic.id !== topicId);
        setSelectedTopic(remainingTopics.length > 0 ? remainingTopics[0].id : "");
      }
      
      // Show success message
      toast.success("Topic deleted successfully.");
    } catch (error) {
      console.error("Failed to delete topic:", error);
      toast.success("Failed to delete topic. Please try again.");
    }
  };

  const topicOptions = topics.map(topic => ({
    value: topic.id,
    label: topic.name
  }));

  const getTopicName = topicId => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : "Unknown Topic";
  };

  // Loading component for topics
  if (loading.topics && topics.length === 0) {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          maxWidth: 1200,
          margin: "0 auto",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          position: "relative"
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2, }}>
            Loading topics...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Loading component for FAQs
  if (loading.faqs && faqs.length === 0 && selectedTopic) {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          maxWidth: 1200,
          margin: "0 auto",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          position: "relative"
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2, }}>
            Loading FAQs...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        margin: "0 auto",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        position: "relative"
      }}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          FAQs Section
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column: Topics */}
        <Grid item xs={12} md={4} sx={{ borderRight: { md: '1px solid #e0e0e0' }, pr: { md: 2 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Topics</Typography>
            <Button variant="contained" size="small" onClick={handleOpenTopicModal} sx={{ textTransform: "none", fontWeight: 600 }}>
              Add Topic
            </Button>
          </Box>
          
          {error.topics && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.topics}
              <Button variant="contained" onClick={() => fetchTopics(1)} size="small" sx={{ ml: 2 }}>Retry</Button>
            </Alert>
          )}

          {isPaginating.topics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>Loading page {topicMeta.page}...</Typography>
            </Box>
          ) : topics.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {topics.map(topic => (
                <Box
                  key={topic.id}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: selectedTopic === topic.id ? "primary.main" : "divider",
                    borderRadius: "8px",
                    position: "relative",
                    cursor: "pointer",
                    backgroundColor: selectedTopic === topic.id ? "primary.light" : "transparent",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }
                  }}
                  onClick={() => setSelectedTopic(topic.id)}
                >
                  <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, zIndex: 1 }}>
                    <IconButton
                      onClick={e => { e.stopPropagation(); handleEditTopic(topic); }}
                      size="small"
                      sx={{ width: 28, height: 28, backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" }, borderRadius: "50%" }}
                    >
                      <RiEdit2Fill size={16} />
                    </IconButton>
                    <IconButton
                      onClick={e => { e.stopPropagation(); handleDeleteTopic(topic.id); }}
                      size="small"
                      sx={{ width: 28, height: 28, backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" }, borderRadius: "50%" }}
                    >
                      <RiDeleteBin6Fill size={16} />
                    </IconButton>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, pr: 6 }}>{topic.name}</Typography>
                </Box>
              ))}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={topicMeta.totalPages}
                  page={topicMeta.page}
                  onChange={handleTopicPageChange}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  disabled={loading.topics || isPaginating.topics}
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>No topics available</Typography>
            </Box>
          )}
        </Grid>

        {/* Right Column: FAQs */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {selectedTopic ? `FAQs for "${getTopicName(selectedTopic)}"` : "FAQs"}
            </Typography>
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleOpenFaqModal} 
              sx={{ textTransform: "none", fontWeight: 600 }}
              disabled={!selectedTopic}
            >
              Add FAQ
            </Button>
          </Box>

          {error.faqs && selectedTopic && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.faqs}
              <Button variant="contained" onClick={() => fetchFaqs(selectedTopic, 1)} size="small" sx={{ ml: 2 }}>Retry</Button>
            </Alert>
          )}

          {!selectedTopic ? (
             <Box sx={{ textAlign: 'center', py: 10 }}>
               <Typography variant="h6" sx={{ color: "text.secondary" }}>Select a topic to view its FAQs</Typography>
             </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isPaginating.faqs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>Loading page {faqMeta.page}...</Typography>
                </Box>
              ) : faqs.length > 0 ? (
                <>
                  {faqs.map(faq => (
                    <Box
                      key={faq.id}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px",
                        position: "relative",
                        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }
                      }}
                    >
                      <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, zIndex: 1 }}>
                        <IconButton
                          onClick={() => handleEditFaq(faq)}
                          size="small"
                          sx={{ width: 28, height: 28, backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" }, borderRadius: "50%" }}
                        >
                          <RiEdit2Fill size={16} />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteFaq(faq.id)}
                          size="small"
                          sx={{ width: 28, height: 28, backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" }, borderRadius: "50%" }}
                        >
                          <RiDeleteBin6Fill size={16} />
                        </IconButton>
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, pr: 7, fontWeight: 600 }}>{faq.question}</Typography>
                      <Typography variant="body1" sx={{ color: "text.primary" }}>{faq.answer}</Typography>
                    </Box>
                  ))}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={faqMeta.totalPages}
                      page={faqMeta.page}
                      onChange={handleFaqPageChange}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      disabled={loading.faqs || isPaginating.faqs}
                      siblingCount={1}
                      boundaryCount={1}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <img src="/img/nodata.png" alt="no data" style={{width:"200px"}} />
                  <Typography variant="h6" sx={{ color: "text.secondary", mt: 2 }}>No FAQs available for this topic</Typography>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Topic Modal */}
      <Modal open={openTopicModal} onClose={handleCloseTopicModal} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ backgroundColor: "white", borderRadius: "8px", padding: 3, width: { xs: "90%", sm: 400 }, maxWidth: "100%", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{isEditMode ? "Edit Topic" : "Add New Topic"}</Typography>
          <TextField label="Topic Name" value={name} onChange={e => setName(e.target.value)} fullWidth />
          <TextField label="Position" type="number" value={topicPosition} onChange={e => setTopicPosition(e.target.value)} fullWidth />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseTopicModal} sx={{ textTransform: "none", color: "text.secondary" }}>Cancel</Button>
            <Button onClick={handleTopicSubmit} variant="contained" sx={{ textTransform: "none", fontWeight: 600 }}>{isEditMode ? "Update" : "Submit"}</Button>
          </Box>
        </Box>
      </Modal>

      {/* FAQ Modal */}
      <Modal open={openFaqModal} onClose={handleCloseFaqModal} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ backgroundColor: "white", borderRadius: "8px", padding: 3, width: { xs: "90%", sm: 500 }, maxWidth: "100%", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{isEditMode ? "Edit FAQ" : "Add New FAQ"}</Typography>
          <TextField select label="Topic" value={faqData.topic} onChange={e => setFaqData({ ...faqData, topic: e.target.value })} fullWidth>
            {topicOptions.map(topic => <MenuItem key={topic.value} value={topic.value}>{topic.label}</MenuItem>)}
          </TextField>
          <TextField label="Question" value={faqData.question} onChange={e => setFaqData({ ...faqData, question: e.target.value })} fullWidth />
          <TextField label="Answer" value={faqData.answer} onChange={e => setFaqData({ ...faqData, answer: e.target.value })} fullWidth multiline rows={4} />
          <TextField label="Position" type="number" value={faqData.position} onChange={e => setFaqData({ ...faqData, position: e.target.value })} fullWidth />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseFaqModal} sx={{ textTransform: "none", color: "text.secondary" }}>Cancel</Button>
            <Button onClick={handleFaqSubmit} variant="contained" sx={{ textTransform: "none", fontWeight: 600 }}>{isEditMode ? "Update" : "Submit"}</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default FAQs;
